import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@/components/base/Alert';
import Button from '@/components/base/Button';
import Modal from '@/components/base/Modal';
import PasswordField from '@/pages/auth/components/PasswordField';
import type { Driver } from '@/types';

interface ResetPasswordModalProps {
  open: boolean;
  driver: Driver | null;
  onClose: () => void;
  onSubmit: (password: string) => Promise<void>;
}

export default function ResetPasswordModal({
  open,
  driver,
  onClose,
  onSubmit,
}: ResetPasswordModalProps) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPassword('');
    setError(null);
    setFormError(null);
    setSaving(false);
  }, [open, driver]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!password) {
      setError(t('drivers.required.password'));
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      await onSubmit(password);
      onClose();
    } catch {
      setFormError(t('drivers.reset.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={saving ? () => undefined : onClose}
      size="sm"
      fullScreen
      title={t('drivers.reset.title')}
      footer={
        <>
          <Button variant="outline" size="md" onClick={onClose} disabled={saving}>
            {t('drivers.form.cancel')}
          </Button>
          <Button type="submit" form="driver-reset-form" variant="primary" size="md" loading={saving}>
            {t('drivers.reset.confirm')}
          </Button>
        </>
      }
    >
      <form id="driver-reset-form" className="space-y-4" onSubmit={handleSubmit} noValidate>
        {formError ? <Alert variant="error">{formError}</Alert> : null}

        {driver ? (
          <div className="flex items-center gap-3 rounded-md border border-background-200 bg-background-100/60 px-3.5 py-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary-500 text-sm font-bold text-background-50">
              {driver.avatarUrl ? (
                <img src={driver.avatarUrl} alt={driver.fullName} className="h-full w-full object-cover" />
              ) : (
                driver.fullName.charAt(0)
              )}
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-foreground-950">{driver.fullName}</p>
              <p className="lb-ltr-field truncate text-xs text-foreground-500">@{driver.username}</p>
            </div>
          </div>
        ) : null}

        <PasswordField
          id="driver-reset-password"
          name="password"
          label={t('drivers.reset.newPassword')}
          requiredMark
          value={password}
          error={error ?? undefined}
          autoComplete="new-password"
          showLabel={t('drivers.form.showPassword')}
          hideLabel={t('drivers.form.hidePassword')}
          onChange={(e) => setPassword(e.target.value)}
        />

        <div className="space-y-1.5 rounded-md border border-secondary-200 bg-secondary-100/60 px-3.5 py-3">
          <p className="flex items-start gap-2 text-xs leading-relaxed text-secondary-900">
            <i className="ri-information-line mt-0.5 text-base text-secondary-700" aria-hidden="true" />
            {t('drivers.reset.note')}
          </p>
          <p className="flex items-start gap-2 text-xs leading-relaxed text-secondary-900">
            <i className="ri-shield-keyhole-line mt-0.5 text-base text-secondary-700" aria-hidden="true" />
            {t('drivers.reset.strengthNote')}
          </p>
        </div>
      </form>
    </Modal>
  );
}